import { useEffect, useMemo, useState } from "react";
import EventApi, { RegisteredCompany } from "../../Api/EventApi";
import { useParams } from "react-router-dom";
import { Button, Modal } from "flowbite-react";

const FEMA_REGISTRATION_LIST_URL =
  "https://www.farmequip.org/meetings/fall/2026/registration/registration-list/";

function escapeCsv(value: string) {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function isFallConventionEvent(name: string) {
  return /fall/i.test(name);
}

function stripHtml(value?: string | null) {
  if (!value) return "";
  return value
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeWebsite(url?: string | null) {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
}

export default function PublicAttendeeList() {
  const { id } = useParams();
  const eventId = Number(id);
  const [companies, setCompanies] = useState<RegisteredCompany[]>([]);
  const [eventName, setEventName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState<RegisteredCompany | null>(null);

  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.href;
  }, []);

  useEffect(() => {
    if (!eventId) {
      setError("Invalid event.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    EventApi.getRegisteredCompanies(eventId)
      .then((res) => {
        setCompanies(res.data.companies ?? []);
        setEventName(res.data.eventName || `Event ${eventId}`);
      })
      .catch(() => {
        setError("Unable to load registered companies for this event.");
        setCompanies([]);
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const exportCsv = () => {
    const safeName = (eventName || `event-${eventId}`)
      .replace(/[^\w-]+/g, "_")
      .slice(0, 60);
    const rows = [
      ["Company", "City", "State", "Website", "Phone"],
      ...companies.map((c) => [
        escapeCsv(c.name ?? ""),
        escapeCsv(c.city ?? ""),
        escapeCsv(c.state ?? ""),
        escapeCsv(c.website ?? ""),
        escapeCsv(c.phone ?? ""),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeName}_registered_companies.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl || window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const website = normalizeWebsite(selected?.website);
  const description = stripHtml(selected?.description);
  const location = [selected?.city, selected?.state].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <p className="text-sm font-medium uppercase tracking-wide text-slate-500">
          Public registration list
        </p>
        <h1 className="mt-1 text-3xl font-bold text-slate-900">
          {eventName || "Event"}
        </h1>
        <h2 className="mt-2 text-lg font-semibold text-slate-700">
          Registered Companies
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Company names only — no individual attendee information. Click a company
          for more details.
        </p>

        {isFallConventionEvent(eventName) && (
          <p className="mt-4 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
            We are partnering with Farm Equipment Manufacturers Association for
            the 2026 Fall Convention.{" "}
            <a
              href={FEMA_REGISTRATION_LIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-600 hover:underline"
            >
              Click here for their full Registration List
            </a>
            .
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            color="success"
            onClick={exportCsv}
            disabled={loading || companies.length === 0}
          >
            Export CSV
          </Button>
          <Button color="light" onClick={copyLink} disabled={!pageUrl}>
            {copied ? "Link copied" : "Copy page link"}
          </Button>
          <span className="text-sm text-slate-500">
            {loading
              ? "Loading…"
              : `${companies.length} compan${companies.length === 1 ? "y" : "ies"}`}
          </span>
        </div>

        {error && (
          <p className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}

        {!loading && !error && companies.length === 0 && (
          <p className="mt-6 text-sm text-slate-600">
            No registered companies yet for this event.
          </p>
        )}

        {!loading && companies.length > 0 && (
          <ol className="mt-6 list-decimal space-y-2 rounded-lg border border-slate-200 bg-white px-8 py-5 text-sm text-slate-800 shadow-sm">
            {companies.map((company) => (
              <li key={`${company.memberId ?? "pub"}-${company.name}`} className="pl-1">
                <button
                  type="button"
                  className="text-left font-medium text-emerald-800 hover:text-emerald-950 hover:underline"
                  onClick={() => setSelected(company)}
                >
                  {company.name}
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      <Modal show={!!selected} onClose={() => setSelected(null)} size="lg">
        <Modal.Header className="text-slate-900 [&_h3]:!text-slate-900">
          {selected?.name ?? "Company"}
        </Modal.Header>
        <Modal.Body>
          {selected ? (
            <div className="space-y-4 text-slate-900">
              {selected.logoUrl ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <img
                    src={selected.logoUrl}
                    alt={selected.name}
                    className="mx-auto max-h-28 max-w-full object-contain"
                  />
                </div>
              ) : null}
              {selected.industry ? (
                <p className="text-sm text-slate-500">{selected.industry}</p>
              ) : null}
              {location ? (
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">Location: </span>
                  {location}
                </p>
              ) : null}
              {selected.phone ? (
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">Phone: </span>
                  <a className="text-emerald-800 hover:underline" href={`tel:${selected.phone}`}>
                    {selected.phone}
                  </a>
                </p>
              ) : null}
              {website ? (
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">Website: </span>
                  <a
                    className="text-emerald-800 hover:underline"
                    href={website}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selected.website}
                  </a>
                </p>
              ) : null}
              {description ? (
                <p className="text-sm leading-relaxed text-slate-700">{description}</p>
              ) : (
                <p className="text-sm text-slate-500">
                  No additional public profile details are available for this company.
                </p>
              )}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setSelected(null)}>
            Close
          </Button>
          {website ? (
            <Button color="success" as="a" href={website} target="_blank" rel="noreferrer">
              Visit website
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
